import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search,
  X,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Grid3X3,
  LayoutList,
  Plus,
  Minus,
  Trash2,
  ArrowLeft,
  Truck,
  AlertCircle,
} from "lucide-react";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useCart } from "../../contexts/CartContext";
import "./online-store.css";

const audFormatter = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
});

/* ------------------------------------------------------------------ */
/* Product card — design markup, old ProductCard behaviour            */
/* ------------------------------------------------------------------ */
function StoreProductCard({ product }) {
  const { addToCart } = useCart();

  // Use _id if available, fallback to id
  const productId = product._id || product.id;

  const primaryImage =
    product.images && product.images.length > 0
      ? product.images[0].src
      : "https://placehold.co/300/300";

  const price = parseFloat(product.price || product.regular_price || 0);
  const regularPrice = parseFloat(product.regular_price || 0);
  const salePrice = product.sale_price ? parseFloat(product.sale_price) : null;

  const isOnSale = salePrice && salePrice < regularPrice;
  const displayPrice = isOnSale ? salePrice : price;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };

  const handleCallForPrice = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Call Umoona Opal Mine phone number
    window.location.href = "tel:+61886724588"; // (08) 8672-4588
  };

  return (
    <Link to={`/opal-store/product/${productId}`} className="st-card">
      <div className="st-card__media">
        <img
          src={primaryImage}
          alt={product.name}
          onError={(e) => {
            e.target.src = "https://placehold.co/300/300";
          }}
          loading="lazy"
          decoding="async"
        />
        <div className="st-card__badges">
          {isOnSale && <span className="st-badge st-badge--sale">SALE</span>}
          {product.featured && <span className="st-badge">FEATURED</span>}
          {product.stock_status === "outofstock" && (
            <span className="st-badge st-badge--oos">OUT OF STOCK</span>
          )}
        </div>
      </div>
      <div className="st-card__body">
        {product.sku && <div className="st-card__sku">SKU: {product.sku}</div>}
        <div className="st-card__name">{product.name}</div>
        <div className="st-card__pricerow">
          {isOnSale ? (
            <>
              <span className="st-card__price">{audFormatter.format(salePrice)}</span>
              <span className="st-card__was">{audFormatter.format(regularPrice)}</span>
            </>
          ) : displayPrice > 0 ? (
            <span className="st-card__price">{audFormatter.format(displayPrice)}</span>
          ) : (
            <span className="st-card__price">Call for Price</span>
          )}
          {product.stock_quantity ? (
            <span className="st-card__stock">{product.stock_quantity} in stock</span>
          ) : null}
        </div>
        {product.categories && product.categories.length > 0 && (
          <div className="st-card__cats">
            {product.categories.slice(0, 2).map((category) => (
              <span key={category.id} className="st-card__cat">
                {category.name}
              </span>
            ))}
          </div>
        )}
        {product.stock_status === "instock" && displayPrice > 0 ? (
          <button type="button" className="st-card__btn" onClick={handleAddToCart}>
            Add to Cart
          </button>
        ) : displayPrice === 0 ? (
          <button type="button" className="st-card__btn" onClick={handleCallForPrice}>
            📞 Call for Price
          </button>
        ) : (
          <button
            type="button"
            className="st-card__btn st-card__btn--disabled"
            disabled
            onClick={(e) => e.preventDefault()}
          >
            Out of Stock
          </button>
        )}
      </div>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Cart drawer — same behaviour as components/cart/CartSidebar.js,    */
/* restyled to the deep-mine design language. Auto-opens on addToCart */
/* (CartContext sets isOpen:true when an item is added).              */
/* ------------------------------------------------------------------ */
function StoreCartDrawer() {
  const {
    items,
    totalItems,
    subtotal,
    isCartOpen,
    setCartOpen,
    removeFromCart,
    updateQuantity,
    clearCart,
    formatPrice,
    getShippingFee,
    getShippingNote,
    getTotal,
  } = useCart();

  const shippingFee = getShippingFee();
  const total = getTotal();

  // Escape closes; lock body scroll while open (matches the old Sheet)
  useEffect(() => {
    if (!isCartOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setCartOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isCartOpen]);

  const handleQuantityChange = (productId, newQuantity) => {
    updateQuantity(productId, newQuantity);
  };

  return (
    <div className={"st-cartroot" + (isCartOpen ? " is-open" : "")} aria-hidden={!isCartOpen}>
      <div className="st-cartoverlay" onClick={() => setCartOpen(false)} />
      <aside className="st-cartpanel" role="dialog" aria-label="Shopping cart">
        <div className="st-cart__head">
          <div className="st-cart__title">
            <ShoppingBag size={17} />
            Shopping Cart ({totalItems} items)
          </div>
          <button
            type="button"
            className="st-cart__close"
            onClick={() => setCartOpen(false)}
            aria-label="Close cart"
          >
            <X size={16} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="st-cart__empty">
            <ShoppingCart size={46} className="st-cart__emptyicon" />
            <div className="st-cart__emptytitle">Your cart is empty</div>
            <p className="st-cart__emptytext">Add some beautiful opals to get started</p>
            <Link
              to="/opal-store"
              className="u-btn u-btn--primary st-cart__fullbtn"
              onClick={() => setCartOpen(false)}
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <>
            <div className="st-cart__items">
              {items.map((item) => {
                const price = parseFloat(item.product.price || item.product.regular_price || 0);
                const itemTotal = price * item.quantity;
                const productId = item.product._id || item.product.id;

                return (
                  <div key={`${productId}-${item.metal_type || ""}`} className="st-cart__item">
                    <div className="st-cart__thumb">
                      {item.product.images && item.product.images.length > 0 ? (
                        <img
                          src={item.product.images[0].src || item.product.images[0]}
                          alt={item.product.name}
                          onError={(e) => {
                            e.target.src = "https://placehold.co/64/64";
                          }}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <ShoppingBag size={20} />
                      )}
                    </div>
                    <div className="st-cart__info">
                      <div className="st-cart__name">{item.product.name}</div>
                      <div className="st-cart__each">{formatPrice(price)} each</div>
                      {item.metal_type && (
                        <div className="st-cart__metal">{item.metal_type}</div>
                      )}
                      <div className="st-cart__qty">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(productId, item.quantity - 1)}
                          aria-label="Decrease quantity"
                        >
                          <Minus size={12} />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(productId, item.quantity + 1)}
                          aria-label="Increase quantity"
                        >
                          <Plus size={12} />
                        </button>
                        <button
                          type="button"
                          className="st-cart__trash"
                          onClick={() => removeFromCart(productId)}
                          aria-label="Remove item"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <div className="st-cart__linetotal">{formatPrice(itemTotal)}</div>
                  </div>
                );
              })}
            </div>

            <div className="st-cart__summary">
              <div className="st-cart__row">
                <span>Subtotal ({totalItems} items):</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="st-cart__row">
                <span>Shipping:</span>
                <span>{shippingFee === 0 ? "FREE" : formatPrice(shippingFee)}</span>
              </div>
              <div className="st-cart__row st-cart__row--total">
                <span>Total:</span>
                <span>{formatPrice(total)}</span>
              </div>
              <div className="st-cart__note">
                <Truck size={14} />
                <span>{getShippingNote()}</span>
              </div>
              <Link
                to="/checkout"
                className="u-btn u-btn--primary st-cart__fullbtn"
                onClick={() => setCartOpen(false)}
              >
                Proceed to Checkout
              </Link>
              <Link
                to="/opal-store"
                className="u-btn u-btn--ghost st-cart__fullbtn"
                onClick={() => setCartOpen(false)}
              >
                Continue Shopping
              </Link>
              {totalItems > 0 && (
                <button
                  type="button"
                  className="st-cart__clear"
                  onClick={() => {
                    clearCart();
                    setCartOpen(false);
                  }}
                >
                  Clear Cart
                </button>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */
const ROnlineStorePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [checkingMaintenance, setCheckingMaintenance] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [sortBy, setSortBy] = useState("featured");
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const productsPerPage = 12;

  // View mode
  const [viewMode, setViewMode] = useState("grid");

  const { items } = useCart();
  const cartItems = items || [];

  useEffect(() => {
    checkMaintenanceMode();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
    updateURL();
  }, [searchTerm, selectedCategory, sortBy, currentPage]);

  const checkMaintenanceMode = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/settings/features/public`
      );
      if (response.ok) {
        const data = await response.json();
        setMaintenanceMode(data.store_maintenance_mode === true);
      }
    } catch (error) {
      console.error("Error checking maintenance mode:", error);
    } finally {
      setCheckingMaintenance(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        // Only show visible categories
        setCategories(data.filter((cat) => cat.visible !== false));
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: productsPerPage.toString(),
        offset: ((currentPage - 1) * productsPerPage).toString(),
      });

      if (searchTerm) params.append("search", searchTerm);
      if (selectedCategory && selectedCategory !== "all")
        params.append("category", selectedCategory);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products?${params}`
      );

      if (response.ok) {
        let data = await response.json();

        // Sort products
        if (sortBy === "price-low") {
          data.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
        } else if (sortBy === "price-high") {
          data.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
        } else if (sortBy === "name") {
          data.sort((a, b) => a.name.localeCompare(b.name));
        } else if (sortBy === "featured") {
          data.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        }

        setProducts(data);

        // Get total count for pagination
        const countResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/products/count?${params}`
        );
        if (countResponse.ok) {
          const countData = await countResponse.json();
          setTotalProducts(countData.count);
        } else {
          setTotalProducts(data.length);
        }
      } else {
        setError("Failed to load products");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setError("Error loading products");
    } finally {
      setLoading(false);
    }
  };

  const updateURL = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (selectedCategory && selectedCategory !== "all") params.set("category", selectedCategory);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setSortBy("featured");
    setCurrentPage(1);
  };

  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();
      setCurrentPage(1);
      fetchProducts();
    },
    [searchTerm]
  );

  const totalPages = Math.ceil(totalProducts / productsPerPage);
  const hasActiveFilters = searchTerm || (selectedCategory && selectedCategory !== "all");

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Store",
    name: "Umoona Opal Mine Online Store",
    description: "Shop authentic Australian opals online from Coober Pedy.",
    url: "https://umoonaopalmine.com.au/online-store",
  };

  // Loading state (maintenance check in flight)
  if (checkingMaintenance) {
    return (
      <MarketingShell pageClass="pg-online-store">
        <section className="st-status">
          <div className="st-spinner" />
          <p className="st-status__text">Loading store...</p>
        </section>
      </MarketingShell>
    );
  }

  // Maintenance mode
  if (maintenanceMode) {
    return (
      <MarketingShell pageClass="pg-online-store">
        <SEOHead
          title="Store Maintenance - Umoona Opal Mine"
          description="Our online store is currently undergoing maintenance."
        />
        <section className="st-status">
          <div className="st-maint">
            <div className="st-maint__icon">
              <ShoppingBag size={40} />
            </div>
            <h1 className="st-maint__title">Store Under Maintenance</h1>
            <p className="st-maint__text">
              We're making improvements. Visit our physical store in Coober Pedy!
            </p>
            <div className="st-maint__btns">
              <Link to="/contact" className="u-btn u-btn--primary">
                Contact Us
              </Link>
              <Link to="/" className="u-btn u-btn--ghost">
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell pageClass="pg-online-store">
      <SEOHead
        title="Shop Australian Opals Online | Umoona Opal Mine Store"
        description="Browse and buy authentic Australian opals online from Coober Pedy. Souvenirs, black light torches, and more."
        canonical="/online-store"
        schemaData={schemaData}
      />

      {/* ============ HERO ============ */}
      <div className="u-hero">
        <img className="u-hero__img" src="/design/assets/store-1.jpg" alt="Authentic Australian opals for sale at the Umoona Opal Store, Coober Pedy" />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">
            Authentic Australian opals · direct from Coober Pedy
          </div>
          <h1 className="u-hero__title">The Umoona Opal Store</h1>
          <p className="u-hero__sub">
            Shop genuine Coober Pedy opal and souvenirs online — each piece
            backed by a certificate of authenticity. Australian shipping.
          </p>
        </div>
      </div>

      {/* ============ CATALOG ============ */}
      <section className="u-sectpad u-sect-deep">
        {/* Toolbar: search / sort / view / filters / cart */}
        <div className="st-toolbar">
          <Link to="/opal-store" className="st-backlink">
            <ArrowLeft size={15} />
            Store Info
          </Link>
          <form className="st-searchwrap" onSubmit={handleSearch}>
            <input
              type="text"
              className="st-search"
              placeholder="Search opals, rings, pendants…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="st-searchclear"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </form>
          <select
            className="st-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort products"
          >
            <option value="featured">Featured</option>
            <option value="name">Name A–Z</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </select>
          <div className="st-viewtoggle">
            <button
              type="button"
              className={viewMode === "grid" ? "is-active" : ""}
              onClick={() => setViewMode("grid")}
              aria-label="Grid view"
            >
              <Grid3X3 size={15} />
            </button>
            <button
              type="button"
              className={viewMode === "list" ? "is-active" : ""}
              onClick={() => setViewMode("list")}
              aria-label="List view"
            >
              <LayoutList size={15} />
            </button>
          </div>
          <button
            type="button"
            className="st-filterbtn"
            onClick={() => setShowFilters(!showFilters)}
            aria-label="Toggle filters"
          >
            <SlidersHorizontal size={15} />
          </button>
          <Link to="/checkout" className="st-cartbtn" aria-label="View cart and checkout">
            <ShoppingBag size={16} />
            {cartItems.length > 0 && <span className="st-cartcount">{cartItems.length}</span>}
          </Link>
        </div>

        <div className="u-split st-layout">
          {/* Sidebar — categories + help */}
          <aside className={"st-side" + (showFilters ? " is-open" : "")}>
            <div className="st-side__label">Categories</div>
            <button
              type="button"
              className={
                "st-cat" +
                (!selectedCategory || selectedCategory === "all" ? " is-active" : "")
              }
              onClick={() => {
                setSelectedCategory("");
                setCurrentPage(1);
              }}
            >
              All Products
            </button>
            {categories.map((category) => (
              <button
                type="button"
                key={category._id || category.id}
                className={"st-cat" + (selectedCategory === category.name ? " is-active" : "")}
                onClick={() => {
                  setSelectedCategory(category.name);
                  setCurrentPage(1);
                }}
              >
                <span>{category.name}</span>
                {category.count > 0 && <span className="st-cat__count">{category.count}</span>}
              </button>
            ))}

            {hasActiveFilters && (
              <button type="button" className="st-clearbtn" onClick={clearFilters}>
                <X size={13} />
                Clear Filters
              </button>
            )}

            <div className="st-help">
              <div className="st-help__title">Need help?</div>
              <div className="st-help__text">
                <a href="tel:+61886725288">Call (08) 8672 5288</a>
                <br />
                Australian shipping only
              </div>
            </div>
          </aside>

          {/* Products column */}
          <div className="st-main">
            <div className="st-results">
              <h2 className="st-results__title">{selectedCategory || "All Products"}</h2>
              <div className="st-results__count">
                {loading
                  ? "Loading..."
                  : `${totalProducts} product${totalProducts !== 1 ? "s" : ""}`}
              </div>
            </div>

            {/* Active filter chips */}
            {hasActiveFilters && (
              <div className="st-chips">
                {searchTerm && (
                  <span className="st-chip">
                    Search: {searchTerm}
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      aria-label="Remove search filter"
                    >
                      <X size={12} />
                    </button>
                  </span>
                )}
                {selectedCategory && selectedCategory !== "all" && (
                  <span className="st-chip">
                    {selectedCategory}
                    <button
                      type="button"
                      onClick={() => setSelectedCategory("")}
                      aria-label="Remove category filter"
                    >
                      <X size={12} />
                    </button>
                  </span>
                )}
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="st-error">
                <AlertCircle size={15} />
                {error}
              </div>
            )}

            {/* Loading skeletons / empty state / product grid */}
            {loading ? (
              <div className={"u-grid3 st-grid" + (viewMode === "list" ? " st-grid--list" : "")}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="st-skel">
                    <div className="st-skel__img" />
                    <div className="st-skel__body">
                      <div className="st-skel__line" />
                      <div className="st-skel__line st-skel__line--short" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="st-empty">
                <div className="st-empty__icon">
                  <ShoppingBag size={34} />
                </div>
                <h3 className="st-empty__title">No products found</h3>
                <p className="st-empty__text">Try adjusting your search or filters</p>
                <button type="button" className="u-btn u-btn--ghost" onClick={clearFilters}>
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className={"u-grid3 st-grid" + (viewMode === "list" ? " st-grid--list" : "")}>
                {products.map((product) => (
                  <StoreProductCard key={product._id || product.id} product={product} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <div className="st-pager">
                <button
                  type="button"
                  className="st-page st-page--nav"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={15} />
                </button>

                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  // Show first, last, current, and adjacent pages
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        type="button"
                        key={page}
                        className={"st-page" + (currentPage === page ? " is-active" : "")}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className="st-pager__dots">
                        …
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  type="button"
                  className="st-page st-page--nav"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">Prefer to see them in person?</h2>
          <p className="u-ctacenter__sub">
            Visit our store in Coober Pedy to browse the full collection with expert guidance.
          </p>
          <div className="u-ctacenter__btns">
            <Link to="/opal-store" className="u-btn u-btn--primary">
              About the Store
            </Link>
            <Link to="/contact" className="u-btn u-btn--ghost">
              Visit Us
            </Link>
          </div>
        </div>
      </section>

      {/* Cart drawer (auto-opens when an item is added) */}
      <StoreCartDrawer />
    </MarketingShell>
  );
};

export default ROnlineStorePage;
